import { useApp } from '../../lib/app';
import { supabase } from '../../lib/supabase';
import { useRows } from '../../lib/useRows';
import { write } from '../../lib/api';
import { LEVELS } from '../../lib/access';
import { Card, Empty, errText, useToast } from '../../ui/kit';

type M = { key: string; name: string; version: string | null; sort: number };
type R = { key: string; name: string; internal: boolean; sort: number };
type G = { id: string; role_key: string; module_key: string; level: number };

export default function Roles() {
  const { access, reload } = useApp();
  const toast = useToast();
  const isSuper = Boolean(access?.super_admin);
  const modules = useRows<M>(() => supabase.from('modules').select('key, name, version, sort').order('sort'), []);
  const roles = useRows<R>(() => supabase.from('roles').select('key, name, internal, sort').is('deleted_at', null).order('sort'), []);
  const grants = useRows<G>(() => supabase.from('role_grants').select('id, role_key, module_key, level').is('deleted_at', null), [], ['role_grants']);

  const cols = roles.rows.filter((r) => r.key !== 'super_admin');
  const get = (role: string, mod: string) => grants.rows.find((g) => g.role_key === role && g.module_key === mod);

  async function set(role: string, mod: string, level: number) {
    const g = get(role, mod);
    try {
      const summary = `Permission ${role} / ${mod} → ${LEVELS[level]}`;
      if (g) await write('role_grants', 'update', { id: g.id, values: { level }, summary });
      else await write('role_grants', 'insert', { values: { role_key: role, module_key: mod, level }, summary });
      await grants.reload();
      void reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  if (modules.loading || roles.loading || grants.loading) return <Empty>Loading…</Empty>;

  return (
    <Card title="What each role can do" pad={false}>
      <p className="muted small" style={{ margin: 0, padding: '12px 16px' }}>
        None · View · Comment · Tick (e.g. contractor marks a defect done) · Edit · Admin. Super Admin has everything. A role given for "All sites"
        applies everywhere; otherwise only on that site. Modules marked with a version arrive in that version.
      </p>
      <div className="table-wrap">
        <table className="t">
          <thead>
            <tr>
              <th>Module</th>
              {cols.map((r) => (
                <th key={r.key} title={r.name}>
                  {r.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.rows.map((m) => (
              <tr key={m.key}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <strong>{m.name}</strong> <span className="muted small">V{m.version}</span>
                </td>
                {cols.map((r) => {
                  const lv = get(r.key, m.key)?.level ?? 0;
                  return (
                    <td key={r.key}>
                      {isSuper ? (
                        <select className="lv-select" aria-label={`${r.name} — ${m.name}`} value={lv} onChange={(e) => void set(r.key, m.key, Number(e.target.value))}>
                          {LEVELS.map((l, i) => (
                            <option key={l} value={i}>
                              {l}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={lv ? '' : 'muted'}>{LEVELS[lv]}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
